import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import {
  BULK_ACTIONS,
  summarizeBulkOutcome,
  type BulkActionId,
  type BulkOutcome,
} from './bulkUserActions';

interface Props {
  action: BulkActionId | null;
  outcome: BulkOutcome | null;
  onClose: () => void;
}

/**
 * Das Ergebnis eines Sammellaufs — vollständig, nicht als Toast.
 *
 * <p>Ein Sammellauf über zwölf Konten kann teilweise scheitern. Ein „Erledigt"-Toast würde
 * genau die Konten verschweigen, wegen denen man noch einmal hinschauen muss; deshalb steht
 * hier jedes gescheiterte und jedes übersprungene Konto mit Namen und Grund.</p>
 */
export function UsersBulkResultDialog({ action, outcome, onClose }: Props) {
  const open = action !== null && outcome !== null;
  const failed = outcome?.failed ?? [];
  const skipped = outcome?.skipped ?? [];

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{action ? BULK_ACTIONS[action].label : 'Sammelaktion'}</DialogTitle>
          <DialogDescription>{outcome ? summarizeBulkOutcome(outcome) : ''}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          {failed.length === 0 && skipped.length === 0 ? (
            <p className="text-ink-soft text-sm">Alle ausgewählten Konten wurden bearbeitet.</p>
          ) : (
            <div className="space-y-5">
              {failed.length > 0 && (
                <OutcomeList title="Fehlgeschlagen" entries={failed} tone="sealwax" />
              )}
              {skipped.length > 0 && (
                <OutcomeList
                  title="Übersprungen"
                  hint="Diese Konten hat der Server für diese Aktion nicht angenommen — sie wurden gar nicht erst versucht."
                  entries={skipped}
                  tone="soft"
                />
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" className="min-h-11" onClick={onClose}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OutcomeList({
  title,
  hint,
  entries,
  tone,
}: {
  title: string;
  hint?: string;
  entries: { id: string; label: string; reason: string }[];
  tone: 'sealwax' | 'soft';
}) {
  return (
    <section>
      <h3
        className={`text-xs font-semibold uppercase tracking-wider ${
          tone === 'sealwax' ? 'text-sealwax' : 'text-ink-soft'
        }`}
      >
        {title} ({entries.length})
      </h3>
      {hint && <p className="text-ink-faint mt-1 text-xs">{hint}</p>}
      <ul className="divide-line/50 mt-2 divide-y">
        {entries.map((entry) => (
          <li key={entry.id} className="py-2">
            <p className="text-ink wrap-break-word text-sm font-medium">{entry.label}</p>
            <p className="text-ink-soft wrap-break-word mt-0.5 text-xs">{entry.reason}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
